/**
 * Store admin — storefront appearance editor (spec 42 R8/R9, design 43; module
 * 22 theming).
 *
 * This screen is a *canvas*, not a form page, so it drops the standard store
 * page header and wears the theme-editor chrome from the vision wireframe
 * (`docs/product/prototypes/storefront-theme-editor.html`): a dark toolbar —
 * actions on the left, a device switch in the centre, visibility on the right —
 * over a white tab panel (Sections · Theme) and a grey canvas holding the live
 * preview in a device frame.
 *
 * Saving stages a *new* version (sections + theme together); buyers see nothing
 * until Publish. Reordering is drag-and-drop (native HTML5 DnD, no new
 * dependency) with Arrow up/down kept as the keyboard-operable path (design 43).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronLeft,
  ExternalLink,
  GripVertical,
  History,
  Monitor,
  Plus,
  Smartphone,
  Tablet,
  Trash2,
} from "lucide-react";
import {
  Button,
  ConfirmDialog,
  Switch,
  Skeleton,
  Alert,
  SidebarTrigger,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Popover,
  PopoverTrigger,
  PopoverContent,
  toast,
} from "@flint/ui";
import {
  MAX_SECTIONS,
  SECTION_LABELS,
  SINGLETON_SECTION_TYPES,
  defaultStoreTheme,
  DEFAULT_HEADER_CONFIG,
  DEFAULT_FOOTER_CONFIG,
  parseSections,
  parseTheme,
  type HomeSection,
  type HomeSectionType,
  type StoreTheme,
} from "@flint/api-contract/store-home";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { SectionForm } from "./section-form.tsx";
import { newSection, summarise } from "./section-defaults.ts";
import { ComposerPreview, type PreviewDevice } from "./composer-preview.tsx";
import { PublishDialog, diffSections } from "./publish-dialog.tsx";
import { StoreProfileCard } from "./store-profile-card.tsx";
import { ThemePanel } from "./theme-panel.tsx";
import { AddSectionModal } from "./add-section-modal.tsx";
import { HeaderForm, FooterForm } from "./chrome-form.tsx";
import { VersionPicker, formatVersionStamp } from "./version-picker.tsx";
import { storeErrorMessage } from "../error-message.ts";

type EditorTab = "sections" | "theme";

/**
 * JSON with object keys sorted — how the editor's draft is compared against the
 * server's copy.
 *
 * A plain `JSON.stringify` comparison is key-order sensitive, and the two sides
 * genuinely disagree on order for identical data. The server's copy arrives via
 * Zod, which rebuilds every object in **schema declaration** order, while the
 * editor appends fields by spread (`{ ...slide, imageUrl: url }`). Uploading a
 * slide image therefore produced
 * `{headline,subhead,ctas,imageUrl}` locally against `{imageUrl,headline,…}`
 * from the server — equal data, unequal strings.
 *
 * The consequence was not cosmetic: `dirty` stayed true forever after any image
 * upload, and Publish is disabled while dirty (publishing a dirty draft would
 * push the previous version). So the seller could save but never publish.
 *
 * Array order is deliberately preserved — the order of sections is the thing
 * being edited. Only object keys are sorted.
 */
function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, val: unknown) => {
    if (!val || typeof val !== "object" || Array.isArray(val)) return val;
    const entries = Object.entries(val as Record<string, unknown>);
    entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return Object.fromEntries(entries);
  });
}

const DEVICES: Array<{ id: PreviewDevice; label: string; Icon: typeof Monitor }> = [
  { id: "desktop", label: "Desktop", Icon: Monitor },
  { id: "tablet", label: "Tablet", Icon: Tablet },
  { id: "mobile", label: "Mobile", Icon: Smartphone },
];

export interface StorefrontComposerPageProps {
  /** Which named appearance is being edited (spec 50 R5). */
  appearanceId: string;
}

export function StorefrontComposerPage({ appearanceId }: StorefrontComposerPageProps) {
  const utils = trpc.useUtils();
  const query = trpc.storeHome.get.useQuery({ appearanceId });
  // Content pages a nav link can point at (module 22). Both drafts and
  // published pages are offered — the link renders regardless; a draft target
  // simply 404s on the storefront until it's published.
  const pagesQuery = trpc.storePages.list.useQuery({});
  const [sections, setSections] = useState<HomeSection[] | null>(null);
  const [theme, setTheme] = useState<StoreTheme | null>(null);
  const [tab, setTab] = useState<EditorTab>("sections");
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The settings form sits below the list; selecting anything (a section, the
  // Header or the Footer) scrolls it into view so the change isn't off-screen.
  const inspectorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected) inspectorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selected]);

  // Seed the editor once the server answers. Deliberately not a `key` remount:
  // a refetch must not throw away edits in progress.
  useEffect(() => {
    if (query.data && sections === null) {
      setSections(query.data.sections);
      setTheme(query.data.theme);
      // Open on the list, not a section — the list is the wireframe's home view.
      setSelected(null);
    }
  }, [query.data, sections]);

  const [publishOpen, setPublishOpen] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  // Scoped to this appearance: an unscoped history would offer to publish a
  // different design's version from inside this canvas (spec 50 R5).
  const versions = trpc.storeHome.versions.useQuery({ appearanceId });

  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * A saved version loaded into the canvas for a look — **not** the draft.
   *
   * Held separately on purpose. The draft lives in `sections`/`theme` and is
   * never touched while previewing, so "Back to my draft" is just
   * `setPreview(null)` and losing work in progress is unrepresentable rather
   * than something restore logic has to get right. It also keeps `dirty`
   * honest: `dirty` compares the draft with the server's copy and never sees
   * the preview.
   */
  const [preview, setPreview] = useState<{
    versionId: string;
    createdAt: string;
    sections: HomeSection[];
    theme: StoreTheme | null;
  } | null>(null);

  // Full compositions only once the picker is actually opened — the metadata
  // query above runs on every editor load and has no use for them.
  const versionContent = trpc.storeHome.versions.useQuery(
    { appearanceId, withContent: true },
    { enabled: pickerOpen },
  );

  /** "Edit this version" over a dirty draft asks first — this gates the ask. */
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);

  /**
   * Make the previewed version the working draft (spec 42 R4's append-only
   * model at work: "editing an old version" loads its content and edits from
   * there — the next Save appends a new row; the old version is never touched).
   * Exiting preview re-derives `panelEditable`, so the panel unlocks itself.
   */
  const applyPreviewAsDraft = () => {
    if (!preview) return;
    setSections(preview.sections);
    setTheme(preview.theme ?? defaultStoreTheme);
    setPreview(null);
    setEditConfirmOpen(false);
    toast.success("Loaded into the editor", {
      description: "Save will keep it as a new version — the original stays in your history.",
    });
  };

  const publish = trpc.storeHome.publish.useMutation({
    onSuccess: () => {
      setPublishError(null);
      setPublishOpen(false);
      // Leaving preview mode on success: the version just went live, so the
      // canvas should go back to showing the merchant's own draft.
      setPreview(null);
      toast.success("Published", { description: "Buyers see this within a minute." });
      void utils.storeHome.get.invalidate();
      void utils.storeHome.versions.invalidate();
    },
    onError: (e) => {
      setPublishError(e.message);
      toast.error("Couldn't publish", { description: storeErrorMessage(e) });
    },
  });

  const save = trpc.storeHome.save.useMutation({
    onSuccess: () => {
      setError(null);
      toast.success("Draft saved", { description: "Publish to put it in front of buyers." });
      void utils.storeHome.get.invalidate();
    },
    onError: (e) => setError(e.message),
  });

  // AC16 / design 43 AD1: view-only members see the layout but no Save or
  // Publish — absent, not greyed. The server gate is `store.catalog.manage`.
  const canEdit = usePermission("store.catalog.manage");
  /**
   * Whether the left panel accepts edits.
   *
   * False while previewing: the canvas is then showing a *saved version*, not
   * the draft, so an edit made there would appear to change what you are
   * looking at while actually mutating something else. Read-only is the honest
   * state — "Back to my draft" is one click away.
   */
  const panelEditable = canEdit && preview === null;
  const list = sections ?? [];
  const current = list.find((s) => s.id === selected) ?? null;

  /** Types already present that may only appear once (R10). */
  const usedSingletons = useMemo(
    () => new Set(list.filter((s) => SINGLETON_SECTION_TYPES.includes(s.type as never)).map((s) => s.type)),
    [list],
  );

  const addable = (Object.keys(SECTION_LABELS) as HomeSectionType[]).filter(
    (t) => !usedSingletons.has(t),
  );

  const full = list.length >= MAX_SECTIONS;

  const mutate = (next: HomeSection[]) => {
    setSections(next);
    setError(null);
  };

  const move = (index: number, delta: number) => {
    const next = [...list];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const [row] = next.splice(index, 1);
    if (row) next.splice(target, 0, row);
    mutate(next);
  };

  // Drag-to-reorder (mouse). The Arrow up/down buttons stay as the
  // keyboard-accessible path; drag is the direct-manipulation affordance the
  // wireframe shows. `dragIndex` is the row being dragged, `overIndex` the
  // insertion point it's hovering.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const reorder = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || to >= list.length) return;
    const next = [...list];
    const [row] = next.splice(from, 1);
    if (row) next.splice(to, 0, row);
    mutate(next);
  };

  if (query.isLoading || sections === null) {
    return (
      <Shell>
        <div className="flex flex-col gap-2 p-6">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </Shell>
    );
  }

  if (query.error) {
    return (
      <Shell>
        <div className="p-6">
          <Alert intent="bad" title="Couldn't load your layout">
            {query.error.message}
          </Alert>
        </div>
      </Shell>
    );
  }

  const currentTheme = theme ?? defaultStoreTheme;
  const dirty =
    stableJson(list) !== stableJson(query.data?.sections ?? []) ||
    stableJson(currentTheme) !== stableJson(query.data?.theme ?? defaultStoreTheme);
  const publishedSections = (query.data?.publishedSections ?? []) as HomeSection[];
  const published = query.data?.origin === "published";
  const originLabel =
    query.data?.origin === "default"
      ? "Default — not published"
      : query.data?.origin === "draft"
        ? "Draft — not published"
        : "Live";

  const versionItems = versions.data?.items ?? [];
  const liveVersion = versionItems.find((v) => v.isPublished);
  const versionLabel = liveVersion
    ? new Date(liveVersion.createdAt).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Default layout";

  return (
    <Shell {...(query.data?.appearanceName ? { appearanceName: query.data.appearanceName } : {})}>
      <PublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        busy={publish.isPending}
        error={publishError}
        isFirstPublish={!query.data?.publishedVersionId}
        diff={diffSections(publishedSections, list)}
        onConfirm={() => {
          const id = query.data?.versionId;
          if (id) publish.mutate({ versionId: id });
        }}
      />

      <ConfirmDialog
        open={editConfirmOpen}
        onOpenChange={setEditConfirmOpen}
        title="Start editing this version?"
        description="Your unsaved changes will be replaced by this saved version. This can't be undone."
        confirmLabel="Edit this version"
        // Default button, like every other confirm in Store admin — the section
        // never uses the red treatment, not even for archiving a product. The
        // description carries the warning instead.
        onConfirm={applyPreviewAsDraft}
      />

      <AddSectionModal
        open={addOpen}
        onOpenChange={setAddOpen}
        addable={addable}
        atCap={full}
        onPick={(type) => {
          const section = newSection(type);
          mutate([...list, section]);
          setSelected(section.id);
          setTab("sections");
          setAddOpen(false);
        }}
      />

      {/* Full-bleed editor — fills the content area; panels scroll internally. */}
      <div className="flex h-full min-h-[640px] flex-col">
        {/* ── Toolbar (dark) ─────────────────────────────────── */}
        <div className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 bg-[#1b2130] px-4 text-slate-100">
          {/* LEFT — actions */}
          <div className="flex items-center gap-3">
            <span className="-ml-1 text-slate-300 hover:text-white">
              <SidebarTrigger />
            </span>
            {/* Back to the library — the canvas replaces the whole page, so
                without this the only way out is the browser button. */}
            <Link
              to="/store/admin/storefront"
              aria-label="Back to appearances"
              className="-ml-1 rounded-sm p-1 text-slate-300 transition hover:bg-white/10 hover:text-white"
            >
              <ChevronLeft size={18} aria-hidden />
            </Link>
            <span className="max-w-[180px] truncate text-sm font-semibold text-white">
              {query.data?.appearanceName ?? "Appearance"}
            </span>
            <span className="hidden text-xs text-slate-400 sm:inline">
              {query.data?.storeName}
            </span>
            {/* Hidden while previewing: what's on the canvas then is a saved
                version, not the draft, so "Save draft" would be a lie. */}
            {canEdit && !preview ? (
              <button
                type="button"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate({ appearanceId, sections: list, theme: currentTheme })}
                className="ml-1 rounded-md border border-white/15 px-3 py-1.5 text-sm font-medium text-slate-100 transition hover:bg-white/10 disabled:opacity-40"
              >
                {save.isPending ? "Saving…" : "Save draft"}
              </button>
            ) : canEdit ? null : (
              <span className="text-xs text-slate-400">View only</span>
            )}
          </div>

          {/* CENTER — device switch */}
          <div
            className="mx-auto flex gap-1 rounded-lg bg-black/25 p-1"
            role="group"
            aria-label="Preview device"
          >
            {DEVICES.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setDevice(id)}
                aria-pressed={device === id}
                title={label}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  device === id ? "bg-white/15 text-white" : "text-slate-400 hover:text-slate-100"
                }`}
              >
                <Icon size={14} aria-hidden />
                <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* RIGHT — visibility */}
          <div className="flex items-center gap-2.5">
            {/* Full-tab preview — a plain anchor because it opens a new tab.
                Read-only, so not gated on manage. Carries the version currently
                loaded on the canvas: without that, picking an old version in
                the history and then clicking Preview showed the *current* saved
                version instead of the one being looked at. */}
            <a
              href={`/store/admin/storefront/${appearanceId}/preview${
                preview ? `?version=${preview.versionId}` : ""
              }`}
              target="_blank"
              rel="noopener"
              title={
                preview
                  ? "Open a full-tab preview of the version you're viewing"
                  : "Open a full-tab preview of your last saved version"
              }
              className="flex items-center gap-2 rounded-md border border-white/15 px-3 py-1.5 text-sm font-medium text-slate-100 transition hover:bg-white/10"
            >
              <ExternalLink size={14} aria-hidden />
              <span className="hidden lg:inline">Preview</span>
            </a>
            {canEdit && versionItems.length > 1 ? (
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    title="Browse saved versions"
                    className="flex items-center gap-2 rounded-md border border-white/15 px-3 py-1.5 text-sm font-medium text-slate-100 transition hover:bg-white/10"
                  >
                    <History size={14} aria-hidden />
                    <span className="hidden max-w-[140px] truncate lg:inline">{versionLabel}</span>
                    <ChevronDown size={14} aria-hidden />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-auto p-0">
                  <VersionPicker
                    // Metadata arrives with the eager query; thumbnails need the
                    // `withContent` one, so prefer it once it has resolved.
                    items={versionContent.data?.items ?? versionItems}
                    loading={versionContent.isLoading}
                    previewingId={preview?.versionId ?? null}
                    onPick={(v) => {
                      setPickerOpen(false);
                      // Loading a version is a *look*, never a publish — that was
                      // the old behaviour and the reason this control was reworked.
                      setPreview({
                        versionId: v.id,
                        createdAt: v.createdAt,
                        sections: parseSections(v.sections ?? []),
                        theme: v.theme ? parseTheme(v.theme) : null,
                      });
                    }}
                  />
                </PopoverContent>
              </Popover>
            ) : null}
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                published ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"
              }`}
            >
              {originLabel}
            </span>
            {canEdit && !preview ? (
              <button
                type="button"
                // Publishing puts a *saved* version live, so unsaved edits must be
                // saved first — publishing a dirty draft would push yesterday's.
                disabled={dirty || !query.data?.versionId || publish.isPending}
                title={dirty ? "Save your draft before publishing." : undefined}
                onClick={() => setPublishOpen(true)}
                className="rounded-md bg-sky-600 px-3.5 py-1.5 text-sm font-semibold text-white transition hover:bg-sky-500 disabled:opacity-40"
              >
                Publish
              </button>
            ) : null}
          </div>
        </div>

        {/* ── Preview bar ────────────────────────────────────────
            Only while a saved version is on the canvas. It has to say plainly
            that this is NOT what buyers see, because the canvas looks exactly
            like the editor's normal state. */}
        {preview ? (
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2">
            <History size={15} className="shrink-0 text-amber-700" aria-hidden />
            <p className="min-w-0 flex-1 text-sm text-amber-900">
              Previewing your saved version from{" "}
              <span className="font-medium">{formatVersionStamp(preview.createdAt)}</span> — this
              isn't what buyers see, and your unsaved edits are safe.
            </p>
            <Button
              intent="secondary"
              size="sm"
              onClick={() => setPreview(null)}
              disabled={publish.isPending}
            >
              Back to my draft
            </Button>
            <Button
              intent="secondary"
              size="sm"
              disabled={publish.isPending}
              // Dirty → confirm first: loading the version replaces unsaved
              // edits, and that must never happen silently.
              onClick={() => (dirty ? setEditConfirmOpen(true) : applyPreviewAsDraft())}
            >
              Edit this version
            </Button>
            <Button
              intent="primary"
              size="sm"
              disabled={publish.isPending || preview.versionId === query.data?.publishedVersionId}
              title={
                preview.versionId === query.data?.publishedVersionId
                  ? "This version is already live."
                  : undefined
              }
              // The existing rollback path: publishing an older id *is* rollback
              // (spec 42 R4). No new domain call, no extra version row.
              onClick={() => publish.mutate({ versionId: preview.versionId })}
            >
              {publish.isPending ? "Publishing…" : "Make this live"}
            </Button>
          </div>
        ) : null}

        {/* ── Body: left tab panel + canvas ──────────────────── */}
        <div className="flex min-h-0 flex-1">
          {/* LEFT PANEL */}
          <aside className="flex w-[300px] shrink-0 flex-col overflow-y-auto border-r border-line bg-paper lg:w-[360px]">
            <div className="flex shrink-0 border-b border-line" role="tablist" aria-label="Editor">
              {(["sections", "theme"] as EditorTab[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  onClick={() => setTab(t)}
                  className={`h-11 flex-1 text-sm font-semibold transition ${
                    tab === t
                      ? "border-b-2 border-sky-600 text-sky-700"
                      : "text-muted hover:text-ink"
                  }`}
                >
                  {t === "sections" ? "Sections" : "Theme"}
                </button>
              ))}
            </div>

            {error ? (
              <Alert intent="bad" title="Couldn't save" className="m-3">
                {error}
              </Alert>
            ) : null}

            {tab === "theme" ? (
              <ThemePanel
                theme={currentTheme}
                canEdit={panelEditable}
                onChange={setTheme}
                storeName={query.data?.storeName ?? "Your store"}
              />
            ) : selected ? (
              /* Inspector — the selected item's settings with a Back button
                 (wireframe push-navigation: the list is replaced, not appended). */
              <div ref={inspectorRef}>
                <div className="flex items-center gap-2 border-b border-line p-3">
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    aria-label="Back to sections"
                    className="-ml-1 rounded-sm p-1 text-muted hover:bg-raised hover:text-ink"
                  >
                    <ChevronLeft size={18} aria-hidden />
                  </button>
                  <span className="text-sm font-semibold text-ink">
                    {selected === "__header__"
                      ? "Header"
                      : selected === "__footer__"
                        ? "Footer"
                        : current
                          ? SECTION_LABELS[current.type]
                          : "Settings"}
                  </span>
                </div>
                <div className="p-3">
                  {selected === "__header__" ? (
                    <HeaderForm
                      value={currentTheme.header ?? DEFAULT_HEADER_CONFIG}
                      canEdit={panelEditable}
                      collections={query.data?.collections ?? []}
                      pages={pagesQuery.data?.items ?? []}
                      hasLogoImage={!!currentTheme.logoUrl}
                      onChange={(h) => setTheme({ ...currentTheme, header: h })}
                    />
                  ) : selected === "__footer__" ? (
                    <FooterForm
                      value={currentTheme.footer ?? DEFAULT_FOOTER_CONFIG}
                      canEdit={panelEditable}
                      onChange={(f) => setTheme({ ...currentTheme, footer: f })}
                    />
                  ) : current ? (
                    <SectionForm
                      section={current}
                      collections={query.data?.collections ?? []}
                      onChange={(next) => mutate(list.map((s) => (s.id === next.id ? next : s)))}
                    />
                  ) : (
                    <p className="text-sm text-muted">This section is no longer here.</p>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Store profile — feeds the hero, stats and about sections.
                    Collapsed by default so the section list is the focus. */}
                <Accordion type="single" collapsible>
                  <AccordionItem value="profile">
                    <AccordionTrigger className="px-3">Store profile</AccordionTrigger>
                    <AccordionContent className="px-3">
                      <StoreProfileCard
                        headerless
                        canEdit={panelEditable}
                        initial={
                          query.data
                            ? {
                                coverImageUrl: query.data.coverImageUrl ?? null,
                                tagline: query.data.profile.tagline ?? null,
                                about: query.data.profile.about ?? null,
                                establishedYear: query.data.profile.establishedYear,
                                teamSize: query.data.profile.teamSize,
                                monthlyCapacity: query.data.profile.monthlyCapacity,
                              }
                            : undefined
                        }
                        onSaved={() => void utils.storeHome.get.invalidate()}
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {/* Section list */}
                <div className="p-2">
                  <button
                    type="button"
                    onClick={() => setSelected("__header__")}
                    className={`flex w-full items-center gap-2 rounded-sm p-2 text-left text-sm font-medium ${
                      selected === "__header__" ? "bg-raised text-sky-700" : "text-ink hover:bg-raised"
                    }`}
                  >
                    Header
                  </button>
                  <div className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
                    Home sections
                  </div>
                  <ul aria-label="Home page sections" className="flex flex-col gap-1">
                    {list.map((section, i) => (
                      <li
                        key={section.id}
                        draggable={panelEditable}
                        onDragStart={(e) => {
                          setDragIndex(i);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => {
                          if (dragIndex === null) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          if (overIndex !== i) setOverIndex(i);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragIndex !== null) reorder(dragIndex, i);
                          setDragIndex(null);
                          setOverIndex(null);
                        }}
                        onDragEnd={() => {
                          setDragIndex(null);
                          setOverIndex(null);
                        }}
                        className={
                          overIndex === i && dragIndex !== null && dragIndex !== i
                            ? "rounded-sm ring-2 ring-accent"
                            : ""
                        }
                      >
                        <div
                          className={`flex items-center gap-1 rounded-sm p-2 ${
                            section.id === selected ? "bg-raised" : ""
                          } ${section.enabled ? "" : "opacity-60"} ${
                            dragIndex === i ? "opacity-40" : ""
                          }`}
                        >
                          <span
                            aria-hidden
                            title="Drag to reorder"
                            className={`shrink-0 text-placeholder ${
                              panelEditable ? "cursor-grab active:cursor-grabbing" : ""
                            }`}
                          >
                            <GripVertical size={14} />
                          </span>
                          <button
                            type="button"
                            onClick={() => setSelected(section.id)}
                            className="min-w-0 flex-1 text-left"
                          >
                            <span className="block truncate text-sm font-medium text-ink">
                              {SECTION_LABELS[section.type]}
                            </span>
                            <span className="block truncate text-xs text-muted">
                              {section.enabled ? summarise(section) : "Hidden from the storefront"}
                            </span>
                          </button>
                          <Switch
                            disabled={!panelEditable}
                            checked={section.enabled}
                            aria-label={`Show ${SECTION_LABELS[section.type]} on the storefront`}
                            onCheckedChange={(v) =>
                              mutate(list.map((s) => (s.id === section.id ? { ...s, enabled: v } : s)))
                            }
                          />
                          <button
                            type="button"
                            aria-label={`Move ${SECTION_LABELS[section.type]} up`}
                            disabled={!panelEditable || i === 0}
                            onClick={() => move(i, -1)}
                            className="rounded-sm p-1 text-muted hover:text-ink disabled:opacity-30"
                          >
                            <ArrowUp size={14} aria-hidden />
                          </button>
                          <button
                            type="button"
                            aria-label={`Move ${SECTION_LABELS[section.type]} down`}
                            disabled={!panelEditable || i === list.length - 1}
                            onClick={() => move(i, 1)}
                            className="rounded-sm p-1 text-muted hover:text-ink disabled:opacity-30"
                          >
                            <ArrowDown size={14} aria-hidden />
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove ${SECTION_LABELS[section.type]}`}
                            disabled={!panelEditable}
                            onClick={() => {
                              mutate(list.filter((s) => s.id !== section.id));
                              if (selected === section.id) setSelected(null);
                            }}
                            className="rounded-sm p-1 text-muted hover:text-bad"
                          >
                            <Trash2 size={14} aria-hidden />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <Button
                    intent="ghost"
                    className="mt-2 w-full"
                    disabled={!panelEditable}
                    onClick={() => setAddOpen(true)}
                  >
                    <Plus size={14} aria-hidden />
                    Add section
                  </Button>

                  <button
                    type="button"
                    onClick={() => setSelected("__footer__")}
                    className={`mt-2 flex w-full items-center gap-2 rounded-sm p-2 text-left text-sm font-medium ${
                      selected === "__footer__" ? "bg-raised text-sky-700" : "text-ink hover:bg-raised"
                    }`}
                  >
                    Footer
                  </button>
                </div>
              </>
            )}

          </aside>

          {/* CANVAS — the preview fills it with a 10px gutter all round; the
              iframe scrolls internally, so the canvas itself never scrolls. */}
          <main className="flex min-w-0 flex-1 overflow-hidden bg-[#eceef2] p-2.5">
            <ComposerPreview
              // Preview when one is loaded, else the draft. The draft is never
              // overwritten, so leaving preview mode restores it exactly.
              sections={preview ? preview.sections : list}
              theme={preview ? (preview.theme ?? defaultStoreTheme) : currentTheme}
              device={device}
              storeName={query.data?.storeName ?? "Your store"}
              storeSlug={query.data?.storeSlug ?? ""}
              collections={query.data?.collections ?? []}
              phone={query.data?.phone ?? null}
              profile={query.data?.profile ?? {
                establishedYear: null,
                teamSize: null,
                monthlyCapacity: null,
                city: null,
                about: null,
              }}
            />
          </main>
        </div>
      </div>
    </Shell>
  );
}

/**
 * Editor shell — the store rail plus a full-bleed content area. No page title:
 * the appearance editor carries its own dark toolbar. Breadcrumbs still say
 * "you are here".
 */
function Shell({ children, appearanceName }: { children: ReactNode; appearanceName?: string }) {
  return (
    <StoreAdminLayout
      hideInsetHeader
      fullBleed
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        // "Appearance" is now a real place — the library — not just a label.
        { label: "Appearance", to: "/store/admin/storefront" },
        ...(appearanceName ? [{ label: appearanceName }] : []),
      ]}
    >
      {children}
    </StoreAdminLayout>
  );
}
